import { Box } from "styled-system/jsx";
import { CloudinaryImage } from "../../atoms";
import { JsxStyleProps } from "styled-system/types";
import { ResizeSimpleAction } from "@cloudinary/transformation-builder-sdk/actions/resize/ResizeSimpleAction";
import { type AssetImage } from "gql/generated.ts";
import {
  selectableImage,
  SelectableImageVariants,
} from "./selectableImage.recipe";
import { useCallback, useRef, useState } from "react";
import { Checkbox, Icon } from "@ttc3k/trekker";
import { Check } from "iconoir-react";
import { motion } from "framer-motion";

type SelectableImageProps = {
  selected: boolean;
  onSelectionChange: (isSelected: boolean) => void;
  image?: Partial<AssetImage>;
  imageStyleProps?: JsxStyleProps & { className?: string };
  resize?: ResizeSimpleAction;
  disabled?: boolean;
} & SelectableImageVariants;

export const SelectableImage = ({
  selected,
  onSelectionChange,
  image,
  imageStyleProps,
  resize,
  disabled,
  size,
  visual,
}: SelectableImageProps) => {
  // Get the recipe
  const classes = selectableImage({ size, visual });
  const [isSelected, setIsSelected] = useState<boolean>(selected);
  const boxRef = useRef<HTMLDivElement>(null);

  const handleToggleCheckbox = useCallback(() => {
    const input = boxRef.current?.querySelector("input") as HTMLInputElement;
    input?.focus();
    input?.click();
  }, []);

  const handleCheckboxChange = useCallback(() => {
    setIsSelected((prevIsSelected) => !prevIsSelected);
    onSelectionChange(isSelected);
  }, [isSelected, onSelectionChange]);

  return (
    <Box
      className={classes.root}
      backgroundColor={image ? "transparent" : "icon.disabled"}
    >
      {image && (
        <CloudinaryImage
          image={image}
          imageStyleProps={{
            aspectRatio: "square",
            objectFit: "cover",
            ...imageStyleProps,
          }}
          resize={resize}
        />
      )}
      <Box
        ref={boxRef}
        className={classes.indicator}
        cursor={disabled ? "default" : "pointer"}
        onClick={handleToggleCheckbox}
      >
        <Checkbox
          srOnly={true}
          disabled={disabled}
          checked={isSelected}
          onChange={handleCheckboxChange}
        />
        {isSelected && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{
              type: "spring",
              bounce: true,
              stiffness: 500,
              damping: 20,
              duration: 0.2,
            }}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
            }}
          >
            <Icon Element={Check} className={classes.icon} />
          </motion.div>
        )}
      </Box>
    </Box>
  );
};
